/* ═══════════════════════════════════════════════════════════
   js/floor-plan-renderer.js — Блок планировки квартиры
   Одна вкладка: «Схема» (план от застройщика).
   Zoom ПО НАЖАТИЮ: кнопки «+ − ⊞», щипок двумя пальцами, нажатие по плану
   (крупно в общем лайтбоксе). Колесо мыши НЕ приближает — оно листает страницу
   (23.08.2026, см. bindEvents). Скачивание изображения.
   ═══════════════════════════════════════════════════════════ */
(() => {
  'use strict';

  let currentState = {
    flatId: 0,
    mode: 'schema',           // Оставлено для совместимости localStorage
    zoom: 1,
    panX: 0,
    panY: 0,
    layoutUrl: '',            // Картинка от застройщика
    buildingName: '',
    flatLabel: ''
  };

  // ═══════════════════════════════════════════════════════
  //  INIT
  // ═══════════════════════════════════════════════════════

  /* ──────────────────────────────────────────────────────────────────────
     СТЕНД: адрес поэтажного плана. На проде — дословно прежний.
     (доводка 3 блока 2.1 плана local-stand, 10.09.2026, находка 27)

     ⛔ ЧТО ЛЕЧИМ. `floor_scheme_url` ставился в `src` как есть — и локальная
     копия сайта шла за схемой этажа НА СЕРВЕР ЗАСТРОЙЩИКА, причём СРАЗУ ПРИ
     ЗАГРУЗКЕ страницы квартиры, без единого клика. Замер 10.09 (без кликов):
     `/flat.php?id=15086353` → `exchange.novostroy-m.ru/files/plans/desc/…`,
     `?id=14582622` → `cdn.brusnika.ru/media/realty/flat/pwh/…`. Прежние замеры
     этого не видели только потому, что у выбранных квартир поле было пустым.

     ⛔ ПОЧЕМУ АДРЕС СЧИТАЕТ ВЫЗЫВАЮЩИЙ. Своего уменьшителя здесь нет и не
     заводим: политика картинок и так живёт в пяти копиях (находка 30). Функцию
     передаёт `flat.js` — он же владеет шириной кадра. Флаг проверяется и здесь:
     без `window.BRONIDOM_STAND === true` адрес возвращается КАК ЕСТЬ, что бы
     вызывающий ни передал, — то есть на проде поведение прежнее до байта.
     ────────────────────────────────────────────────────────────────────── */
  let standSrc = (u) => u;

  function init(container, flatData, opts) {
    if (!container || !flatData) return;

    standSrc = (window.BRONIDOM_STAND === true && opts && typeof opts.standSrc === 'function')
      ? opts.standSrc
      : (u) => u;

    currentState.flatId = flatData.id;
    // block #28 fix-round-2: раздел «Поэтажный план» = ОТДЕЛЬНЫЙ поэтажник (схема этажа),
    // сервер отдаёт его в flat.floor_scheme_url (НЕ layout_url — иначе дублировали бы
    // планировку из галереи). Пусто → раздел скрыт (innerHTML='' ниже). Владелец 04.07.
    // Гейт безопасности URL на клиенте (тот же, что flat-overlay.js): пускаем только
    // https + непустой host (относительный путь резолвится в наш https-origin). Блокирует
    // javascript:/http:/протокол-relative/битые, даже если сервер что-то пропустил.
    currentState.layoutUrl = isSafeImageUrl(flatData.floor_scheme_url) ? flatData.floor_scheme_url : '';
    currentState.buildingName = flatData.building_name || '';
    const rooms = parseInt(flatData.rooms, 10);
    currentState.flatLabel = (rooms === 0 ? 'Студия' : rooms + 'к') + '_' +
      parseFloat(flatData.area).toFixed(0) + 'м';

    if (!currentState.layoutUrl) {
      container.innerHTML = '';
      return;
    }

    // Чистим устаревшее значение localStorage от старой 3D-вкладки.
    const saved = localStorage.getItem('fp_mode');
    if (saved && saved !== 'schema') {
      localStorage.setItem('fp_mode', 'schema');
    }

    renderUI(container);
  }

  // ═══════════════════════════════════════════════════════
  //  UI RENDERING
  // ═══════════════════════════════════════════════════════

  function renderUI(container) {
    const layoutUrl = currentState.layoutUrl;
    const esc = escHtml;

    // jk-redesign 6.1j: нижняя секция переименована «Планировка»→«Поэтажный план»
    // (галерея-hero = «Планировка квартиры» первой; эта секция ниже = поэтажный план).
    let html = '<h2 class="flat-section-title">Поэтажный план</h2>';
    html += '<div class="floor-plan-container" id="fpContainer">';

    // Toolbar: zoom
    html += '<div class="floor-plan-toolbar">';
    html += '<div class="floor-plan-tabs" id="fpTabs"></div>';

    html += '<div class="floor-plan-controls">';
    html += '<div class="floor-plan-zoom">';
    html += '<button class="floor-plan-zoom-btn" id="fpZoomOut" title="Уменьшить">−</button>';
    html += '<button class="floor-plan-zoom-btn" id="fpZoomIn" title="Увеличить">+</button>';
    html += '<button class="floor-plan-zoom-btn" id="fpZoomFit" title="По размеру">⊞</button>';
    html += '</div>';
    html += '</div>';
    html += '</div>'; // toolbar

    // Paper — контейнер для изображения
    html += '<div class="floor-plan-paper" id="fpPaper">';

    // Схема (план от застройщика) — единственный активный контент.
    // БЕЗ loading="lazy": это основной контент блока, а lazy loader
    // иногда не триггерится когда у img нет явной высоты.
    html += '<div class="floor-plan-original floor-plan-original--active" id="fpSchema">';
    // ⛔ standSrc() зовётся ПРЯМО В СТРОКЕ, а не через переменную выше: сторож
    // отрисовщиков (tests/Integration/local-stand-isolation.test.mjs) читает
    // исходник, а не исполняет его.
    html += '<img src="' + esc(standSrc(layoutUrl)) + '" alt="Планировка от застройщика" decoding="async">';
    html += '</div>';

    html += '</div>'; // paper

    // Скачать
    html += '<div class="floor-plan-export">';
    html += '<button class="floor-plan-export-btn" id="fpExportPng"><i class="fa-solid fa-download"></i> Скачать</button>';
    html += '</div>';

    html += '</div>'; // container

    container.innerHTML = html;

    // Сбросить зум на актуальный
    applyZoom();
    bindEvents(container);
  }

  // ═══════════════════════════════════════════════════════
  //  ZOOM / PAN
  // ═══════════════════════════════════════════════════════

  function applyZoom() {
    const paper = document.getElementById('fpPaper');
    if (!paper) return;
    const active = paper.querySelector('.floor-plan-original--active img');
    if (active) {
      active.style.transform = 'scale(' + currentState.zoom + ')';
      active.style.transformOrigin = 'center center';
      active.style.transition = 'transform 0.2s ease';
    }
  }

  function zoomIn() {
    currentState.zoom = Math.min(4, currentState.zoom + 0.25);
    applyZoom();
  }

  function zoomOut() {
    currentState.zoom = Math.max(0.5, currentState.zoom - 0.25);
    applyZoom();
  }

  function zoomFit() {
    currentState.zoom = 1;
    currentState.panX = 0;
    currentState.panY = 0;
    applyZoom();
  }

  // ═══════════════════════════════════════════════════════
  //  EVENTS
  // ═══════════════════════════════════════════════════════

  function bindEvents(container) {
    // Zoom buttons
    const zoomInBtn = document.getElementById('fpZoomIn');
    const zoomOutBtn = document.getElementById('fpZoomOut');
    const zoomFitBtn = document.getElementById('fpZoomFit');
    if (zoomInBtn) zoomInBtn.addEventListener('click', zoomIn);
    if (zoomOutBtn) zoomOutBtn.addEventListener('click', zoomOut);
    if (zoomFitBtn) zoomFitBtn.addEventListener('click', zoomFit);

    // ⛔ КОЛЕСО МЫШИ НАД ПОЭТАЖНИКОМ БОЛЬШЕ НЕ ПРИБЛИЖАЕТ (23.08.2026, слово владельца:
    //    «когда листаешь — поэтажник был зафиксирован, не приближался, пока на него не нажмёшь»).
    //
    //    Здесь стоял `wheel` с `e.preventDefault()`: колесо над блоком перехватывалось
    //    НАСОВСЕМ. Замер 23.08 на зеркале, одно движение колеса над «Поэтажным планом»:
    //        прокрутка страницы — ОТМЕНЕНА (defaultPrevented = true)
    //        поэтажник           — вырос до scale(1,25)
    //        страница            — осталась на месте
    //    Человек листал страницу, указатель проезжал над планом — и страница вставала,
    //    а план начинал наплывать. Выглядело как «страница зависла».
    //
    //    Приближение НЕ ПОТЕРЯНО, оно стало ПО НАЖАТИЮ:
    //      · кнопки «+ − ⊞» выше — работают как работали;
    //      · щипок двумя пальцами ниже — осознанный жест, его не делают случайно;
    //      · нажатие по самому плану открывает его крупно в общем лайтбоксе (ниже).
    //
    // ⛔ Слушатель НЕ заменён на «пустой passive», а УБРАН целиком: отсутствие
    //    обработчика и есть штатная прокрутка. Пустой слушатель был бы лишней работой
    //    на каждом кадре прокрутки и поводом вернуть в него зум «на одну строчку».
    const paper = document.getElementById('fpPaper');

    // Нажатие по поэтажнику — открыть крупно. Лайтбокс общий (js/utils.js): там
    // уже есть и приближение, и перетаскивание, и белая подложка под чертёж (kinds:['plan']).
    // Кнопки панели и ссылки исключены: у них своё действие.
    if (paper) {
      paper.addEventListener('click', e => {
        const t = e.target;
        if (t && t.closest && t.closest('button, a, .floor-plan-toolbar')) return;
        if (!currentState.layoutUrl || typeof window.openLightbox !== 'function') return;
        // standSrc уходит и в лайтбокс: иначе «крупно» открывало бы тот же
        // адрес застройщика, который мы только что увели на стенде.
        window.openLightbox([currentState.layoutUrl], 0, { kinds: ['plan'], standSrc: standSrc });
      });
    }

    // Touch pinch-zoom
    if (paper) {
      let lastDist = 0;
      paper.addEventListener('touchstart', e => {
        if (e.touches.length === 2) {
          lastDist = Math.hypot(
            e.touches[0].clientX - e.touches[1].clientX,
            e.touches[0].clientY - e.touches[1].clientY
          );
        }
      });
      paper.addEventListener('touchmove', e => {
        if (e.touches.length === 2) {
          e.preventDefault();
          const dist = Math.hypot(
            e.touches[0].clientX - e.touches[1].clientX,
            e.touches[0].clientY - e.touches[1].clientY
          );
          if (lastDist > 0) {
            const ratio = dist / lastDist;
            currentState.zoom = Math.max(0.5, Math.min(4, currentState.zoom * ratio));
            applyZoom();
          }
          lastDist = dist;
        }
      }, { passive: false });
    }

    // Double-click zoom fit
    if (paper) {
      paper.addEventListener('dblclick', e => {
        e.preventDefault();
        zoomFit();
      });
    }

    // Export
    const exportBtn = document.getElementById('fpExportPng');
    if (exportBtn) exportBtn.addEventListener('click', downloadImage);
  }

  // ═══════════════════════════════════════════════════════
  //  EXPORT
  // ═══════════════════════════════════════════════════════

  function downloadImage() {
    const paper = document.getElementById('fpPaper');
    if (!paper) return;

    const img = paper.querySelector('.floor-plan-original--active img');
    if (!img) return;

    const a = document.createElement('a');
    a.href = img.src;
    a.download = buildFileName('png');
    a.target = '_blank';
    a.click();
  }

  function buildFileName(ext) {
    const name = (currentState.buildingName || 'plan').replace(/[^a-zA-Zа-яА-ЯёЁ0-9_-]/g, '_');
    const label = currentState.flatLabel || '';
    return 'планировка_' + name + '_' + label + '.' + ext;
  }

  // ═══════════════════════════════════════════════════════
  //  UTILITIES
  // ═══════════════════════════════════════════════════════

  function escHtml(s) {
    if (!s) return '';
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  // Валидация URL картинки перед рендером (SSOT с flat-overlay.js:31 — урок конвейера #5,
  // scheme/host). Разрешаем только https + непустой host; относительный путь резолвится в
  // наш https-origin. Блокирует http:/javascript:/data:/протокол-relative/битые.
  function isSafeImageUrl(u) {
    if (typeof u !== 'string' || u === '') return false;
    // База нужна только для относительных URL; в среде без location (тест-песочница)
    // абсолютные https-URL обязаны проходить так же, как в браузере.
    const base = (typeof location !== 'undefined' && location.origin) ? location.origin : 'https://bronidom.ru';
    try {
      const p = new URL(u, base);
      return p.protocol === 'https:' && p.hostname !== '';
    } catch { return false; }
  }

  // ═══════════════════════════════════════════════════════
  //  PUBLIC API
  // ═══════════════════════════════════════════════════════

  window.FloorPlanRenderer = { init };
})();
